/**
 * features/admin/ParentSettings.tsx
 * ------------------------------------------------------------------
 * Admin screen for configuring the parent portal and managing
 * parent invitations.
 *
 * - Toggle discipline visibility for parents
 * - Search guardians with emails → send invitations
 * - View invitation history
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
// Round 2: parent unlink
// Round 1: parent UX (issues 1, 2, 4)
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
// (swap only — saveSettings is called from quick toggles; the
// existing inline toast UX is the right pattern there. The import
// swap is still important so the foundation wrapper neutralises
// the bug class.)
import { setDoc, addDoc } from '@/lib/firebase';
import {
  Settings,
  Mail,
  Bell,
  Megaphone,
  Wrench,
  EyeOff,
  Send,
  Check,
  Clock,
  AlertCircle,
  CheckCircle2,
  ShieldAlert,
  Search,
  UserPlus,
  XCircle,
  // Round 2: parent unlink
  UserCog,
  Link2Off,
  Loader2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { db } from '@/lib/firebase';
import { functions } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
  FormModal,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import type { SchoolSettings, ParentInvitation, Guardian } from '@/types';

export default function ParentSettings() {
  const { students, users } = useData();
  const { userData } = useAuth();

  const [settings, setSettings] = useState<SchoolSettings>({
    id: 'default',
    parentDisciplineVisible: true,
  });
  const [invitations, setInvitations] = useState<ParentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState<string | null>(null);
  // Editable maintenance messages (saved on button click, not per keystroke).
  const [noticeMsg, setNoticeMsg] = useState('');
  const [maintMsg, setMaintMsg] = useState('');
  const BALANCE_MSG = 'Fee balances are being updated and made current. Please check again in 24 hours.';

  // Load settings + invitations
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const settingsSnap = await getDoc(doc(db, COLLECTIONS.schoolSettings, 'default'));
        if (settingsSnap.exists()) {
          const s = settingsSnap.data() as SchoolSettings;
          setSettings(s);
          setNoticeMsg(s.parentNotice?.message ?? '');
          setMaintMsg(s.parentPortalMaintenance?.message ?? '');
        }

        const invSnap = await getDocs(
          query(collection(db, COLLECTIONS.parentInvitations), orderBy('createdAt', 'desc')),
        );
        setInvitations(invSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as ParentInvitation));
      } catch (e) {
        console.error('Failed to load parent settings:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Save settings
  async function saveSettings(patch: Partial<SchoolSettings>) {
    const updated = { ...settings, ...patch, updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? '' };
    setSaving(true);
    try {
      await setDoc(doc(db, COLLECTIONS.schoolSettings, 'default'), updated);
      setSettings(updated);
      setMsg({ ok: true, text: 'Settings saved.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  // Find guardians with email across all students
  const guardiansWithEmail = useMemo(() => {
    const results: Array<{
      guardian: Guardian;
      studentId: string;
      studentName: string;
      className: string;
    }> = [];
    for (const s of students) {
      const guardians = s.admissions?.guardians ?? [];
      for (const g of guardians) {
        if (g.email) {
          results.push({
            guardian: g,
            studentId: s.id,
            studentName: s.fullName,
            className: s.currentClass,
          });
        }
      }
    }
    return results;
  }, [students]);

  // Group by guardian email (a guardian might have multiple children)
  const guardianGroups = useMemo(() => {
    const map = new Map<string, typeof guardiansWithEmail>();
    for (const item of guardiansWithEmail) {
      const key = item.guardian.email!.toLowerCase();
      const group = map.get(key) ?? [];
      group.push(item);
      map.set(key, group);
    }
    return Array.from(map.entries()).map(([email, items]) => {
      const allStudentIds = items.map((i) => i.studentId);
      const existingInvite = invitations.find(
        (inv) => inv.email.toLowerCase() === email && inv.status !== 'expired',
      );
      // Students already linked in a prior invitation
      const linkedStudentIds = existingInvite?.studentIds ?? [];
      // New students not yet in the invitation
      const newStudentIds = allStudentIds.filter((id) => !linkedStudentIds.includes(id));
      return {
        email,
        guardianName: items[0].guardian.fullName,
        guardianId: items[0].guardian.id,
        students: items.map((i) => ({ id: i.studentId, name: i.studentName })),
        alreadyInvited: !!existingInvite,
        hasNewChildren: existingInvite ? newStudentIds.length > 0 : false,
        newStudentIds,
      };
    });
  }, [guardiansWithEmail, invitations]);

  const filteredGuardians = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return guardianGroups;
    return guardianGroups.filter(
      (g) =>
        g.guardianName.toLowerCase().includes(q) ||
        g.email.includes(q) ||
        g.students.some((s) => s.name.toLowerCase().includes(q)),
    );
  }, [guardianGroups, search]);

  // Round 1: parent UX (issues 1, 2, 4) — class-grouping helpers
  // Map studentId -> className so we can tag invitations and
  // guardian groups by the children's class. We use the students
  // list from useData() rather than re-fetching.
  const classNameByStudentId = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of students) {
      m.set(s.id, s.currentClass || '');
    }
    return m;
  }, [students]);

  // Bucket items (guardians or invitations) by the classes their
  // children belong to. Items spanning >1 class go under the
  // synthetic 'Multi-class' bucket, which is rendered first.
  function groupByClass<T extends { students?: { id: string }[]; studentIds?: string[] }>(
    items: T[],
  ): { className: string; items: T[] }[] {
    const buckets = new Map<string, T[]>();
    for (const item of items) {
      const ids =
        item.students?.map((s) => s.id) ?? item.studentIds ?? [];
      const classes = new Set<string>();
      for (const id of ids) {
        const cn = classNameByStudentId.get(id);
        if (cn) classes.add(cn);
      }
      const key =
        classes.size === 0
          ? 'Unassigned'
          : classes.size === 1
          ? Array.from(classes)[0]
          : 'Multi-class';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(item);
    }
    // Multi-class first, then Unassigned last, alpha in between
    const sortedKeys = Array.from(buckets.keys()).sort((a, b) => {
      if (a === 'Multi-class') return -1;
      if (b === 'Multi-class') return 1;
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => ({ className: k, items: buckets.get(k)! }));
  }

  // Display map for invitation status (issue 2).
  // The 'accepted' label was misleading because the parent
  // never accepts anything — the admin clicks Process and an
  // auth account is created on their behalf. Renamed in UI only;
  // schema/CF code still uses 'accepted'.
  const INVITATION_STATUS_LABEL: Record<string, string> = {
    pending: 'Pending',
    accepted: 'Account created',
    expired: 'Expired',
  };

  const groupedGuardians = useMemo(
    () => groupByClass(filteredGuardians),
    [filteredGuardians, classNameByStudentId],
  );
  const groupedInvitations = useMemo(
    () => groupByClass(invitations),
    [invitations, classNameByStudentId],
  );

  // Round 2: parent unlink — manage-parent modal state
  // Holds the invitation whose students we're managing. We use
  // the invitation as the unit because that's what the
  // Invitation History row corresponds to; the underlying CF
  // takes (parentUid, studentId).
  const [manageInv, setManageInv] = useState<ParentInvitation | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null); // studentId in flight

  // Find the parent user doc that corresponds to this invitation.
  // Prefer the invitation's recorded parentUid, fall back to a
  // guardianId match on the users collection.
  function findParentUid(inv: ParentInvitation): string | null {
    if (inv.parentUid) return inv.parentUid;
    const match = users.find(
      (u) =>
        u.role === 'parent' &&
        u.parentAccount?.guardianId === inv.guardianId,
    );
    return match?.uid ?? null;
  }

  async function unlinkStudent(inv: ParentInvitation, studentId: string) {
    const parentUid = findParentUid(inv);
    if (!parentUid) {
      setMsg({
        ok: false,
        text: 'Could not resolve the parent account for this invitation.',
      });
      return;
    }
    setUnlinking(studentId);
    try {
      const fn = httpsCallable(functions, 'removeChildLink');
      await fn({ parentUid, studentId });
      // Re-pull the invitations list so the UI reflects what the
      // server did. Cheap because parentInvitations is small.
      const invSnap = await getDocs(
        query(
          collection(db, COLLECTIONS.parentInvitations),
          orderBy('createdAt', 'desc'),
        ),
      );
      const refreshed = invSnap.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as ParentInvitation,
      );
      setInvitations(refreshed);
      // Update the modal's reference too, so it shows the post-
      // unlink state and auto-closes if everyone is unlinked.
      const updatedInv = refreshed.find((i) => i.id === inv.id);
      if (!updatedInv || (updatedInv.studentIds || []).length === 0) {
        setManageInv(null);
      } else {
        setManageInv(updatedInv);
      }
      setMsg({ ok: true, text: 'Student unlinked from parent account.' });
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setUnlinking(null);
    }
  }


  // Send invitation
  async function sendInvite(guardian: typeof guardianGroups[0]) {
    setSending(guardian.email);
    try {
      // Create invitation record
      const invitation: Omit<ParentInvitation, 'id'> = {
        guardianId: guardian.guardianId,
        guardianName: guardian.guardianName,
        email: guardian.email,
        studentIds: guardian.students.map((s) => s.id),
        studentNames: guardian.students.map((s) => s.name),
        status: 'pending',
        invitedBy: userData?.uid ?? '',
        invitedByName: userData?.displayName ?? '',
        createdAt: new Date().toISOString(),
      };
      const ref = await addDoc(collection(db, COLLECTIONS.parentInvitations), invitation);

      // Try to call the Cloud Function to create the account + send email
      try {
        const createParent = httpsCallable(functions, 'createParentAccount');
        await createParent({
          invitationId: ref.id,
          email: guardian.email,
          guardianName: guardian.guardianName,
          guardianId: guardian.guardianId,
          studentIds: guardian.students.map((s) => s.id),
        });
      } catch (fnErr) {
        console.warn('Cloud function not deployed yet, invitation created:', fnErr);
      }

      setInvitations((prev) => [{ ...invitation, id: ref.id }, ...prev]);
      setMsg({ ok: true, text: `Invitation sent to ${guardian.email}` });
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSending(null);
    }
  }

  if (loading) return <Spinner label="Loading parent settings…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Settings}
        title="Parent Portal Settings"
        subtitle="Configure parent access and manage invitations"
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {msg.text}
          </div>
        </SoftCard>
      )}

      {/* Settings toggles */}
      <SoftCard>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Settings size={18} className="text-[#2d5295]" />
          Portal Configuration
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <ShieldAlert size={18} className="text-rose-500" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">
                  Discipline Visibility
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  When enabled, parents can see their child's discipline records
                </p>
              </div>
            </div>
            <button
              onClick={() => saveSettings({ parentDisciplineVisible: !settings.parentDisciplineVisible })}
              disabled={saving}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                settings.parentDisciplineVisible
                  ? 'bg-emerald-500'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  settings.parentDisciplineVisible ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Absence alerts */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <Bell size={18} className="text-amber-500" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">
                  Absence Alerts
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Notify parents (in-app + push) the same day their child is marked absent
                </p>
              </div>
            </div>
            <button
              onClick={() => saveSettings({ parentAbsenceAlerts: settings.parentAbsenceAlerts === false })}
              disabled={saving}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                settings.parentAbsenceAlerts !== false ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  settings.parentAbsenceAlerts !== false ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Absence email (only meaningful when alerts are on) */}
          <div className={`flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 ${settings.parentAbsenceAlerts === false ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-[#2d5295]" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">
                  Email Absence Alerts
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Also send an email (in addition to the in-app + push alert)
                </p>
              </div>
            </div>
            <button
              onClick={() => saveSettings({ parentAbsenceEmail: settings.parentAbsenceEmail === false })}
              disabled={saving || settings.parentAbsenceAlerts === false}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                settings.parentAbsenceEmail !== false ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  settings.parentAbsenceEmail !== false ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
      </SoftCard>

      {/* Parent portal — maintenance controls */}
      <SoftCard>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <Wrench size={18} className="text-amber-500" />
          Parent Portal — Maintenance
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Display-only controls for parents. These never change fee-arrears academic holds — a family on hold stays on hold regardless.
        </p>

        <div className="space-y-4">
          {/* Notice banner */}
          <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Megaphone size={18} className="text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">Notice banner</p>
                  <p className="text-xs text-gray-500 mt-0.5">Show a message to all parents across the portal</p>
                </div>
              </div>
              <button
                onClick={() => saveSettings({ parentNotice: { enabled: !(settings.parentNotice?.enabled), message: noticeMsg } })}
                disabled={saving}
                className={`relative w-12 h-7 rounded-full transition-colors ${settings.parentNotice?.enabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${settings.parentNotice?.enabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
            <textarea
              value={noticeMsg}
              onChange={(e) => setNoticeMsg(e.target.value)}
              rows={2}
              placeholder="e.g. Fee balances are being updated…"
              className="mt-3 w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white"
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                onClick={() => saveSettings({ parentNotice: { enabled: settings.parentNotice?.enabled ?? false, message: noticeMsg } })}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primaryDark disabled:opacity-50"
              >
                Save message
              </button>
              <button
                onClick={() => setNoticeMsg(BALANCE_MSG)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Use balance-update message
              </button>
            </div>
          </div>

          {/* Hide fee balances */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <EyeOff size={18} className="text-[#2d5295]" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">Hide fee balances</p>
                <p className="text-xs text-gray-500 mt-0.5">Parents see "balances are being updated" instead of amounts. Arrears holds still apply.</p>
              </div>
            </div>
            <button
              onClick={() => saveSettings({ parentFeesVisible: settings.parentFeesVisible === false })}
              disabled={saving}
              className={`relative w-12 h-7 rounded-full transition-colors ${settings.parentFeesVisible === false ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${settings.parentFeesVisible === false ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Full maintenance */}
          <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Wrench size={18} className="text-rose-500" />
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">Full maintenance mode</p>
                  <p className="text-xs text-gray-500 mt-0.5">Parents see only a maintenance screen (staff unaffected)</p>
                </div>
              </div>
              <button
                onClick={() => saveSettings({ parentPortalMaintenance: { enabled: !(settings.parentPortalMaintenance?.enabled), message: maintMsg } })}
                disabled={saving}
                className={`relative w-12 h-7 rounded-full transition-colors ${settings.parentPortalMaintenance?.enabled ? 'bg-rose-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${settings.parentPortalMaintenance?.enabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
            <textarea
              value={maintMsg}
              onChange={(e) => setMaintMsg(e.target.value)}
              rows={2}
              placeholder="Message shown on the maintenance screen (optional)"
              className="mt-3 w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white"
            />
            <button
              onClick={() => saveSettings({ parentPortalMaintenance: { enabled: settings.parentPortalMaintenance?.enabled ?? false, message: maintMsg } })}
              disabled={saving}
              className="mt-2 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primaryDark disabled:opacity-50"
            >
              Save message
            </button>
          </div>
        </div>
      </SoftCard>

      {/* Send invitations */}
      <SoftCard>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <UserPlus size={18} className="text-[#2d5295]" />
          Send Parent Invitations
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Guardians with email addresses from the admissions records. Send an invitation to create their parent account.
        </p>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by guardian name, email, or student name…"
            className={glassFieldClass('pl-9')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filteredGuardians.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            {guardianGroups.length === 0
              ? 'No guardians with email addresses found. Add emails in the Admissions module.'
              : 'No matches found.'}
          </p>
        ) : (
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {groupedGuardians.map((grp) => (
              <div key={grp.className} className="space-y-2">
                <div className="sticky top-0 z-[1] bg-white dark:bg-gray-800 -mx-1 px-1 py-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {grp.className}{' '}
                    <span className="text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal">
                      · {grp.items.length}
                    </span>
                  </p>
                </div>
                {grp.items.map((g) => (
              <div
                key={g.email}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">
                    {g.guardianName}
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Mail size={11} /> {g.email}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Children: {g.students.map((s) => s.name).join(', ')}
                  </p>
                </div>
                {g.alreadyInvited && !g.hasNewChildren ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                    <Check size={14} /> Invited
                  </span>
                ) : (
                  <AccentButton
                    variant="soft"
                    icon={Send}
                    onClick={() => sendInvite(g)}
                    disabled={sending === g.email}
                  >
                    {sending === g.email ? 'Sending…' : g.hasNewChildren ? 'Add Children' : 'Invite'}
                  </AccentButton>
                )}
              </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </SoftCard>

      {/* Invitation history */}
      {invitations.length > 0 && (
        <SoftCard>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Mail size={18} className="text-[#2d5295]" />
            Invitation History
          </h3>
          <div className="space-y-4">
            {groupedInvitations.map((grp) => (
              <div key={grp.className} className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {grp.className}{' '}
                  <span className="text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal">
                    · {grp.items.length}
                  </span>
                </p>
                {grp.items.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">
                    {inv.guardianName}
                  </p>
                  <p className="text-xs text-gray-400">{inv.email}</p>
                  <p className="text-xs text-gray-400">
                    {inv.studentNames.join(', ')} · {new Date(inv.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {/* Round 2: parent unlink — Manage button */}
                <div className="flex items-center gap-2">
                  {inv.status === 'accepted' && (
                    <button
                      onClick={() => setManageInv(inv)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 text-gray-600 dark:text-gray-300"
                      title="Manage which children are linked to this parent account"
                    >
                      <UserCog size={12} /> Manage
                    </button>
                  )}
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${
                  inv.status === 'accepted'
                    ? 'bg-emerald-100 text-emerald-700'
                    : inv.status === 'expired'
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {inv.status === 'accepted' && <Check size={12} />}
                  {inv.status === 'pending' && <Clock size={12} />}
                  {inv.status === 'expired' && <XCircle size={12} />}
                  {INVITATION_STATUS_LABEL[inv.status] ?? inv.status}
                </span>
                </div>
              </div>
                ))}
              </div>
            ))}
          </div>
        </SoftCard>
      )}

      {/* Round 2: parent unlink — manage modal */}
      <FormModal
        open={!!manageInv}
        onClose={() => { if (unlinking === null) setManageInv(null); }}
        icon={UserCog}
        title={manageInv ? `Manage children for ${manageInv.guardianName}` : 'Manage children'}
        maxWidth="max-w-md"
      >
        {manageInv && (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Unlinking a child removes their reports from this parent
              account immediately on all devices. The parent account
              itself stays active.
            </p>
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {(manageInv.studentIds || []).map((sid, idx) => {
                const name = (manageInv.studentNames || [])[idx] ?? sid;
                const className = classNameByStudentId.get(sid) || '';
                const inFlight = unlinking === sid;
                return (
                  <div
                    key={sid}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        {name}
                      </p>
                      {className && (
                        <p className="text-xs text-gray-400">{className}</p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Unlink ${name} from ${manageInv.guardianName}? ` +
                              'The parent will lose access immediately on all devices.',
                          )
                        ) {
                          unlinkStudent(manageInv, sid);
                        }
                      }}
                      disabled={unlinking !== null}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40"
                    >
                      {inFlight ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Link2Off size={12} />
                      )}
                      Unlink
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </FormModal>
    </div>
  );
}
